import { MouseEvent, FC } from 'react';

import { ToggleButtonGroup, Typography } from '@mui/material';
import { Variant } from '@mui/material/styles/createTypography';
import { TypographyPropsVariantOverrides } from '@mui/material/Typography/Typography';
import { OverridableStringUnion } from '@mui/types';

import { Testable } from 'utils/TypeUtils';

import { StyledToggleButton } from 'components/UIkit/atoms/Button/Toggle/StyledToggleButton';

type Option = {
  value: string | number;
  label: string;
  disabled?: boolean;
} & Testable;

export interface MultiToggleProps {
  options: Option[];
  value: string[] | number[];
  onChange: (newFormats: string[] | number[]) => void;
  fullWidth?: boolean;
  disabled?: boolean;
  isError?: boolean;
  variant?: 'primary' | 'secondary';
}

export const MultiToggleButton: FC<MultiToggleProps> = ({
  options,
  value: selectedValues,
  onChange,
  fullWidth = false,
  disabled = false,
  isError = false,
  variant = 'primary'
}) => {
  const handleChange = (_event: MouseEvent<HTMLElement>, newFormats: string[] | number[]) => {
    onChange(newFormats);
  };

  return (
    <ToggleButtonGroup
      value={selectedValues}
      onChange={handleChange}
      color={isError ? 'error' : 'primary'}
      fullWidth={fullWidth}
      disabled={disabled}
    >
      {options.map(({ value: optionValue, label, disabled = false, testHook }) => {
        const isSelected = selectedValues.includes(optionValue as never);
        let typographyVariant: OverridableStringUnion<
          Variant | 'inherit',
          TypographyPropsVariantOverrides
        > = 'form-text';

        if (variant === 'secondary') {
          if (isSelected) {
            typographyVariant = 'body1';
          } else {
            typographyVariant = 'body2';
          }
        }
        return (
          <StyledToggleButton
            key={optionValue}
            value={optionValue}
            selected={selectedValues.includes(optionValue as never)}
            disabled={disabled}
            data-test-hook={testHook}
            variant={variant}
          >
            <Typography variant={typographyVariant}>{label}</Typography>
          </StyledToggleButton>
        );
      })}
    </ToggleButtonGroup>
  );
};
